import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  Optional,
  Self,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  FormControl,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { DateFormatLocalizationService } from 'src/app/core/services/date-format-localization.service';

type DateSelectionType = 'completeDate' | 'monthYear' | 'year';

class DateInputSelectionState {
  private readonly selectionTypeSubject = new BehaviorSubject<DateSelectionType>('completeDate');
  readonly selectionType$ = this.selectionTypeSubject.asObservable();

  get selectionType(): DateSelectionType {
    return this.selectionTypeSubject.value;
  }

  set selectionType(value: DateSelectionType) {
    this.selectionTypeSubject.next(value);
  }
}

class DynamicDateAdapter extends NativeDateAdapter {
  constructor(
    private dateFormatService: DateFormatLocalizationService,
    private selectionState: DateInputSelectionState,
    locale: string
  ) {
    super(locale);
  }

  private isYearFirstFormat(displayFormat: string): boolean {
    const format = displayFormat.toUpperCase();
    const yearIndex = format.indexOf('YYYY');
    const monthIndex = Math.max(format.indexOf('MM'), format.indexOf('M'));
    return yearIndex !== -1 && monthIndex !== -1 && yearIndex < monthIndex;
  }

  private getMonthYearFormat(): { format: string; yearFirst: boolean } {
    const config = this.dateFormatService.getCurrentConfig();
    const displayFormat = config.displayFormat.toUpperCase();
    const yearFirst = this.isYearFirstFormat(displayFormat);

    // Detect the separator used in the format
    const separatorMatch = displayFormat.match(/[^\w]/);
    const separator = separatorMatch ? separatorMatch[0] : '/';

    if (yearFirst) {
      return { format: `YYYY${separator}MM`, yearFirst: true };
    }

    return { format: `MM${separator}YYYY`, yearFirst: false };
  }

  override format(date: Date, displayFormat: object): string {
    const format = displayFormat as string | object;
    if (format === 'input' || format === 'dateA11yLabel') {
      if (this.selectionState.selectionType === 'monthYear') {
        const { format: monthYearFormat, yearFirst } = this.getMonthYearFormat();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        if (yearFirst) {
          return `${year}${monthYearFormat.charAt(4)}${month}`;
        }

        return `${month}${monthYearFormat.charAt(2)}${year}`;
      }

      if (this.selectionState.selectionType === 'year') {
        return date.getFullYear().toString();
      }

      return this.dateFormatService.formatDate(date);
    }
    return super.format(date, displayFormat);
  }

  override parse(value: string | number | Date | null | undefined): Date | null {
    if (typeof value === 'string' && value.trim()) {
      const trimmed = value.trim();

      if (this.selectionState.selectionType === 'year') {
        const yearMatch = /^(\d{4})$/.exec(trimmed);
        if (yearMatch) {
          return new Date(Number(yearMatch[1]), 0, 1);
        }
      }

      if (this.selectionState.selectionType === 'monthYear') {
        const { yearFirst } = this.getMonthYearFormat();

        if (yearFirst) {
          // Try YYYY/MM or YYYY-MM format
          const yearMonthMatch = /^(\d{4})[\/-](\d{1,2})$/.exec(trimmed);
          if (yearMonthMatch) {
            const year = Number(yearMonthMatch[1]);
            const month = Number(yearMonthMatch[2]);

            if (month >= 1 && month <= 12) {
              return new Date(year, month - 1, 1);
            }
          }
        } else {
          // Try MM/YYYY or MM-YYYY format
          const monthYearMatch = /^(\d{1,2})[\/-](\d{4})$/.exec(trimmed);
          if (monthYearMatch) {
            const month = Number(monthYearMatch[1]);
            const year = Number(monthYearMatch[2]);

            if (month >= 1 && month <= 12) {
              return new Date(year, month - 1, 1);
            }
          }
        }

        // Fallback: try both patterns
        const monthYearMatch = /^(\d{1,2})[\/-](\d{4})$/.exec(trimmed);
        if (monthYearMatch) {
          const month = Number(monthYearMatch[1]);
          const year = Number(monthYearMatch[2]);

          if (month >= 1 && month <= 12) {
            return new Date(year, month - 1, 1);
          }
        }

        const yearMonthMatch = /^(\d{4})[\/-](\d{1,2})$/.exec(trimmed);
        if (yearMonthMatch) {
          const year = Number(yearMonthMatch[1]);
          const month = Number(yearMonthMatch[2]);

          if (month >= 1 && month <= 12) {
            return new Date(year, month - 1, 1);
          }
        }
      }

      return this.dateFormatService.parseDate(value);
    }
    return super.parse(value);
  }
}

const DYNAMIC_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'input',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'input',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (dateFormatService: DateFormatLocalizationService) =>
        dateFormatService.getMatDateLocale(),
      deps: [DateFormatLocalizationService],
    },
    {
      provide: DateAdapter,
      useClass: DynamicDateAdapter,
      deps: [DateFormatLocalizationService, DateInputSelectionState, MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DYNAMIC_DATE_FORMATS },
    DateInputSelectionState,
  ],
})
export class DateInputComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() label: string = 'common.date';
  @Input() showLabel = true;
  @Input() fullWidth = true;
  @Input() id?: string;
  @Input() height = '41px';
  @Input() hint?: string;
  @Input() showFormatHint = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() required = false;
  @Input() placeholder = 'common.selectDate';
  @Input() startView: 'month' | 'year' | 'multi-year' = 'month'; // Which view to show when the datepicker is opened
  @Input()
  set allowSelectionType(value: DateSelectionType) {
    this._allowSelectionType = value;
    this.selectionState.selectionType = value;
  }
  get allowSelectionType(): DateSelectionType {
    return this._allowSelectionType;
  }
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (this._disabled) {
      this.dateControl.disable({ emitEvent: false });
      return;
    }
    this.dateControl.enable({ emitEvent: false });
  }
  get disabled(): boolean {
    return this._disabled;
  }
  @Input() clearable = false;

  @Output() dateChange = new EventEmitter<Date | null>();

  dateControl: FormControl<Date | null> = new FormControl<Date | null>(null);
  formatLabel$: Observable<string>;
  readonly displayFormatHint: string;

  private readonly destroy$ = new Subject<void>();
  private _disabled = false;
  private _allowSelectionType: DateSelectionType = 'completeDate';
  private onChange: (value: Date | null) => void = () => { };
  private onTouched: () => void = () => { };

  private isYearFirstFormat(displayFormat: string): boolean {
    const format = displayFormat.toUpperCase();
    const yearIndex = format.indexOf('YYYY');
    const monthIndex = Math.max(format.indexOf('MM'), format.indexOf('M'));
    return yearIndex !== -1 && monthIndex !== -1 && yearIndex < monthIndex;
  }

  constructor(
    private readonly dateFormatService: DateFormatLocalizationService,
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly selectionState: DateInputSelectionState,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.formatLabel$ = combineLatest([
      this.dateFormatService.dateFormatConfig$,
      this.selectionState.selectionType$
    ]).pipe(
      map(([config, selectionType]) => {
        if (selectionType === 'monthYear') {
          const yearFirst = this.isYearFirstFormat(config.displayFormat);
          return yearFirst ? 'YYYY/MM' : 'MM/YYYY';
        }

        if (selectionType === 'year') {
          return 'YYYY';
        }

        return config.displayFormat;
      }),
      takeUntil(this.destroy$)
    );

    this.displayFormatHint = this.dateFormatService.getDisplayFormatLabel();

    this.dateFormatService.dateFormatConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        this.dateAdapter.setLocale(config.matDateLocale);
      });
  }

  ngOnInit(): void {
    this.dateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.dateChange.emit(value);
        this.onChange(value);
      });

    if (this.disabled) {
      this.dateControl.disable({ emitEvent: false });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required'] || changes['minDate'] || changes['maxDate']) {
      this.syncValidators();
    }
  }

  writeValue(value: Date | string | null): void {
    if (!value) {
      this.dateControl.setValue(null, { emitEvent: false });
      return;
    }

    const dateValue = typeof value === 'string'
      ? this.dateAdapter.parse(value, 'input')
      : value;
    this.dateControl.setValue(dateValue, { emitEvent: false });
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  markTouched(): void {
    this.onTouched();
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.dateControl.disable({ emitEvent: false })
      : this.dateControl.enable({ emitEvent: false });
  }

  yearSelectedHandle(dateTime: Date, datepicker: MatDatepicker<Date>): void {
    // Only allow year selection if startView is multi-year and allowSelectionType is year
    if (this.startView !== 'multi-year' || this.allowSelectionType !== 'year') return;

    this.dateControl.setValue(dateTime, { emitEvent: true });
    datepicker.close();
  }

  monthSelectedHandle(dateTime: Date, datepicker: MatDatepicker<Date>): void {
    if (this.startView === 'month' || this.allowSelectionType !== 'monthYear') return;

    this.dateControl.setValue(dateTime, { emitEvent: true });
    datepicker.close();
  }

  onDateSelected(event: MatDatepickerInputEvent<Date>): void {
    const value = event.value;
    this.dateControl.setValue(value, { emitEvent: true });
    this.onTouched();
  }

  clearDate(event: Event): void {
    event.stopPropagation();
    this.dateControl.setValue(null, { emitEvent: true });
    this.onTouched();
  }

  private minDateValidator(minDate: Date): ValidatorFn {
    return (control: AbstractControl<Date | null>) => {
      const value = control.value;
      if (!value) return null;
      return value >= minDate ? null : { minDate: { min: minDate, actual: value } };
    };
  }

  private maxDateValidator(maxDate: Date): ValidatorFn {
    return (control: AbstractControl<Date | null>) => {
      const value = control.value;
      if (!value) return null;
      return value <= maxDate ? null : { maxDate: { max: maxDate, actual: value } };
    };
  }

  private syncValidators(): void {
    const validators: ValidatorFn[] = [];

    if (this.required) {
      validators.push(Validators.required);
    }

    if (this.minDate) {
      validators.push(this.minDateValidator(this.minDate));
    }

    if (this.maxDate) {
      validators.push(this.maxDateValidator(this.maxDate));
    }

    this.dateControl.setValidators(validators);
    this.dateControl.updateValueAndValidity({ emitEvent: false });
  }

  get isInvalid(): boolean {
    return this.dateControl.invalid && this.dateControl.touched;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
